import {
  useCallback,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  AsyncSection,
  Button,
  DashboardWidget,
  EntityDashboard,
  KpiCatalogProvider,
  QuickAccessCatalogProvider,
  Skeleton,
  SolveCatalogProvider,
  VsmCatalogProvider,
  WidgetActionsProvider,
  WidgetCard,
  allWidgets,
  type DashboardItem,
  type QuickAccessView,
  type WidgetActions,
} from "@playbookai/ui";
import {
  dashboardWidgetKindsForType,
  SolveActionsOutputSchema,
  type QuickAccessParams,
} from "@playbookai/widget-schemas";
import type { WidgetKind } from "@playbookai/types";
import { useShellBridge } from "@playbookai/solve-feature";
import { useAuthStore } from "../auth/auth-store.js";
import { firstName, formatToday } from "../home/greeting.js";
import { WidgetDataPreview } from "@playbookai/dashboard-client";
import type { WidgetDataResolveResponse } from "@playbookai/types";
import {
  HOME_CONNECTION_SELECTION,
  useHomeDashboard,
} from "../home/useHomeDashboard.js";
import { isHomeWidgetAvailable } from "../home/widget-availability.js";
import { useKpiCatalog } from "../home/useKpiCatalog.js";
import { useVsmCatalog } from "../home/useVsmCatalog.js";
import { useSolveCatalog } from "../home/useSolveCatalog.js";
import { createWidgetStateResolver } from "../home/resolveWidgetView.js";
import { getEnabledModules } from "../modules/index.js";
import { availableModuleSet } from "../modules/availability.js";
import { manifestsToLauncherEntries } from "../modules/launcher-entries.js";
import { NoModulesEmptyState } from "../modules/NoModulesEmptyState.js";

const HOME_WIDGET_DESCRIPTIONS: Partial<Record<WidgetKind, string>> = {
  quickAccess: "Modules enabled for you in this workspace",
  mySolves: "Your recent open problem-solving work",
  myVsms: "Value streams you recently worked in",
  myTasks: "Work that needs your attention",
  projectPulse: "Recent movement across active work",
  singleKpi: "Latest performance against its target",
  chart: "A recent measure trend",
  kpiWatchlist: "Measures you follow",
  kpiTable: "Measures you follow",
  moodCheck: "Private to you",
  dailyBrief: "A concise view of what needs attention",
  coachingNudge: "A practical next step for today",
  strategicHealth: "Progress against strategic priorities",
  kaizenTable: "Improvements you own across value streams",
  solveActions: "Actions from your SOLVEs — open one to edit it on its canvas",
};

/**
 * Home — the Personal Home dashboard over the LIVE dashboard capability
 * (ADR-0054): configuration in `dashboard.*` via the shared client, widget
 * content through the batched widget-data endpoint, per-widget bounded
 * loading/empty/error states (§16.4). `EntityDashboard` (DS, data-blind) owns
 * the grid; the host owns persistence, resolution, and the params→view
 * resolver. Greeting/date are page chrome; My SOLVEs and Quick Access are
 * ordinary configurable widget instances in the persisted grid.
 */
export function Home(): ReactElement {
  const claims = useAuthStore((s) => s.claims);
  const greetingName = firstName(claims?.name ?? claims?.email);
  const navigate = useNavigate();
  const { openSolve, openModuleEntityTab } = useShellBridge();
  const [conflictNotice, setConflictNotice] = useState(false);
  const [errorNotice, setErrorNotice] = useState(false);

  const onConflict = useCallback(() => setConflictNotice(true), []);
  const onError = useCallback(() => setErrorNotice(true), []);
  const dashboard = useHomeDashboard({ onConflict, onError });
  const kpiCatalog = useKpiCatalog();
  const vsmCatalog = useVsmCatalog();
  /**
   * The owner VOCABULARY the SOLVE Actions editor offers, taken from the same
   * resolved payloads the cards render. There is no endpoint that lists owner
   * text — it is free text inside two canvas widgets — so the resolved data is
   * the only honest source. Instances are unioned because the edit dialog does
   * not tell the host which one it is editing, and offering a slightly wider
   * vocabulary is strictly better than offering a narrower one.
   */
  const solveActionOwners = useMemo<readonly string[]>(() => {
    const results = dashboard.widgetData?.results;
    if (!results) return [];
    const seen = new Set<string>();
    const owners: string[] = [];
    for (const item of dashboard.layout) {
      if (item.kind !== "solveActions") continue;
      const slot = results[item.instanceId]?.["actions"];
      if (slot?.status !== "ready") continue;
      const parsed = SolveActionsOutputSchema.safeParse(slot.data);
      if (!parsed.success) continue;
      for (const owner of parsed.data.ownerOptions) {
        const key = owner.trim().toLocaleLowerCase();
        if (key === "" || seen.has(key)) continue;
        seen.add(key);
        owners.push(owner);
      }
    }
    return owners;
  }, [dashboard.layout, dashboard.widgetData]);
  const solveCatalog = useSolveCatalog(solveActionOwners);
  // One resolver instance per mount: an unchanged slot result must yield the
  // SAME view object, or `DashboardWidget` discards its optimistic state on
  // every render (Mood Check flickering back to "not logged").
  const resolveWidgetState = useMemo(() => createWidgetStateResolver(), []);

  const enabledModules = useMemo(
    () => getEnabledModules(claims?.enabledModules ?? []),
    [claims?.enabledModules],
  );
  // Installable keys, so "Add widget" leaves out the KPI widgets while KPI
  // Manager is off (ADR-0100).
  const enabledModuleIds = useMemo(
    () => availableModuleSet(claims?.enabledModules ?? []),
    [claims?.enabledModules],
  );

  // The dashboard-type catalogue from the PURE contract registry, intersected
  // with the kinds this shell build has React bodies for — Add menu can't
  // offer a kind the server rejects or the registry can't render.
  const catalog = useMemo<WidgetKind[]>(() => {
    const personal = new Set(dashboardWidgetKindsForType("personal_home"));
    return allWidgets()
      .filter(
        (d) =>
          d.dashboard &&
          personal.has(d.kind) &&
          isHomeWidgetAvailable(d.kind, enabledModuleIds),
      )
      .map((d) => d.kind);
  }, [enabledModuleIds]);
  const moduleEntries = useMemo(
    () => manifestsToLauncherEntries(enabledModules),
    [enabledModules],
  );
  const solveModule = enabledModules.find((module) => module.id === "solve");
  const vsmModule = enabledModules.find((module) => module.id === "vsm");
  const quickAccessModules = useMemo<QuickAccessView["modules"]>(
    () =>
      moduleEntries.map((entry) => ({
        id: entry.id,
        name: entry.name,
        blurb: entry.blurb,
        accent: entry.accent,
        icon: entry.icon,
      })),
    [moduleEntries],
  );
  const quickAccessCatalogValue = useMemo(
    () => ({ modules: quickAccessModules }),
    [quickAccessModules],
  );
  const actions = useMemo<WidgetActions>(
    () => ({
      // Hand a user-intent prompt to the Player AI rail via the shell's window
      // event (AppLayout listens). NOT an LLM call — the seed is the user's
      // question (ADR-0007/0012).
      askPlayerAI: (prompt: string) =>
        window.dispatchEvent(
          new CustomEvent("playerai:open", { detail: { prompt } }),
        ),
      ...(solveModule && openSolve
        ? {
            openSolve: (solveId: string, title: string) =>
              openSolve({ solveId, title }),
          }
        : {}),
      ...(solveModule
        ? { openSolveHome: () => navigate(solveModule.routeBase) }
        : {}),
      /* Deep-link a SOLVE Actions row to the widget it came from. The shell
         owns tab identity, so this goes through the bridge exactly like
         `openSolve`; the `?widget=` hint is a REQUEST the workspace may
         decline (deleted widget, hidden phase), never a promise. Offered only
         when SOLVE is enabled AND the bridge can open a tab, so the widget
         renders display-only rather than dead buttons otherwise. */
      ...(solveModule && openSolve
        ? {
            openSolveAction: (target: {
              solveId: string;
              solveTitle: string;
              widgetId: string;
            }) =>
              openSolve({
                solveId: target.solveId,
                title: target.solveTitle,
                search: `?widget=${encodeURIComponent(target.widgetId)}`,
              }),
          }
        : {}),
      ...(vsmModule
        ? {
            openVsm: (vsmId: string, title: string) => {
              if (openModuleEntityTab) {
                openModuleEntityTab({
                  moduleId: "vsm",
                  entityId: vsmId,
                  title,
                });
              } else {
                navigate(`${vsmModule.routeBase}/${encodeURIComponent(vsmId)}`);
              }
            },
            openVsmHome: () => navigate(vsmModule.routeBase),
          }
        : {}),
      openModule: (moduleId) => {
        // The widget receives no route. Revalidate the opaque id against the
        // current authorized module set at the moment of navigation.
        const module = enabledModules.find(
          (candidate) => candidate.id === moduleId,
        );
        if (module) navigate(module.routeBase);
      },
    }),
    [
      enabledModules,
      navigate,
      openModuleEntityTab,
      openSolve,
      solveModule,
      vsmModule,
    ],
  );

  const renderWidget = useCallback(
    (
      item: DashboardItem,
      data: WidgetDataResolveResponse | undefined = dashboard.widgetData,
      loading = dashboard.widgetDataLoading,
    ): ReactNode => {
      const description = HOME_WIDGET_DESCRIPTIONS[item.kind];
      const definition = allWidgets().find(
        (widget) => widget.kind === item.kind,
      );
      const resolvedTitle = item.title ?? definition?.label;
      const boundsLongList =
        item.kind === "kaizenTable" || item.kind === "solveActions";
      const frameProps = {
        ...(description ? { description } : {}),
        ...(boundsLongList
          ? {
              className: "max-h-[26rem]",
              bodyClassName: "overflow-y-auto overscroll-contain",
            }
          : {}),
      };
      const fallbackFrameProps = {
        ...frameProps,
        ...(description && definition?.dashboard?.icon
          ? { icon: definition.dashboard.icon }
          : {}),
      };
      if (item.kind === "quickAccess") {
        const params = (item.params ?? {}) as QuickAccessParams;
        const modules =
          params.mode === "selected"
            ? (params.selectedModuleIds ?? [])
                .map((id) =>
                  quickAccessModules.find((module) => module.id === id),
                )
                .filter(
                  (module): module is QuickAccessView["modules"][number] =>
                    module !== undefined,
                )
            : quickAccessModules;
        return (
          /* The onboarding tour's apps step points at Quick Access, and the
             widget is removable — so the marker sits on a wrapper the host
             owns rather than inside the data-blind widget body. When the
             widget is gone the target is simply absent, which is exactly what
             the step's `use-available` policy is for. */
          <div data-tour-id="home.quick-access">
            <DashboardWidget
              kind="quickAccess"
              data={{ modules }}
              {...frameProps}
              {...(item.title !== undefined ? { title: item.title } : {})}
            />
          </div>
        );
      }
      const resolved = resolveWidgetState(item, data, loading);
      const frame = (children: ReactNode): ReactNode => (
        <WidgetCard
          {...(resolvedTitle !== undefined ? { title: resolvedTitle } : {})}
          {...fallbackFrameProps}
        >
          {children}
        </WidgetCard>
      );
      switch (resolved.state) {
        case "loading":
          return frame(
            <div aria-busy="true" className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>,
          );
        case "empty":
        case "invalid":
          /* Kept as today's 14px line: EmptyState size="sm" sets 11px type,
             a look change no PR 3 row approves. */
          return frame(
            <p className="text-sm text-pb-text-muted">{resolved.message}</p>,
          );
        case "error":
          /* A widget whose data failed to load: the one-line error row
             through AsyncSection (OK-31). */
          return frame(
            <AsyncSection
              status="error"
              size="sm"
              errorTitle="This widget’s data isn’t available right now."
              {...(resolved.retryable
                ? { onRetry: dashboard.retryWidgetData }
                : {})}
            >
              {null}
            </AsyncSection>,
          );
        case "view":
          if (resolved.kind === "moodCheck") {
            const current = resolved.view;
            return (
              <DashboardWidget
                kind={resolved.kind}
                data={current}
                onChange={(next) => {
                  // TWO independent commands behind ONE change channel, and
                  // each fires only on its own axis actually moving. Hiding
                  // must never write a mood entry, and logging must never
                  // change visibility — sending both on every change is
                  // exactly how one would start doing the other's job.
                  const nextHidden = next.hidden ?? false;
                  if (nextHidden !== (current.hidden ?? false)) {
                    dashboard.onSetMoodHidden(nextHidden);
                    return;
                  }
                  if (
                    next.logged &&
                    (next.logged.score !== current.logged?.score ||
                      next.logged.note !== (current.logged?.note ?? null))
                  ) {
                    dashboard.onLogMood(next.logged);
                  }
                }}
                {...frameProps}
                {...(item.title !== undefined ? { title: item.title } : {})}
              />
            );
          }
          return (
            <DashboardWidget
              kind={resolved.kind}
              data={resolved.view}
              {...frameProps}
              {...(item.title !== undefined ? { title: item.title } : {})}
            />
          );
      }
    },
    [dashboard, quickAccessModules, resolveWidgetState],
  );

  // No modules enabled for this workspace → short-circuit the whole page to a
  // single empty state (My SOLVEs + dashboard are moot with nothing enabled).
  const hasModules = enabledModules.length > 0;

  const renderPreview = (item: DashboardItem): ReactNode => {
    const config = HOME_CONNECTION_SELECTION[item.kind];
    if (!config || !dashboard.record || !dashboard.page)
      return renderWidget(item);
    const selection = Object.fromEntries(
      config.paramKeys.flatMap((key) =>
        item.params?.[key] !== undefined ? [[key, item.params[key]]] : [],
      ),
    );
    if (
      config.emptyRemovesConnection &&
      Object.values(selection).every(
        (value) =>
          value === "" ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0),
      )
    ) {
      return (
        <WidgetCard
          title={
            item.title ??
            allWidgets().find((widget) => widget.kind === item.kind)?.label ??
            item.kind
          }
        >
          <p className="py-4 text-sm text-pb-text-muted">
            Choose{" "}
            {item.kind === "kpiTable" || item.kind === "kpiWatchlist"
              ? "KPIs"
              : "a KPI"}{" "}
            to see a preview.
          </p>
        </WidgetCard>
      );
    }
    return (
      <WidgetDataPreview
        scope={{
          tenantId: claims?.tenantId ?? "none",
          userId: claims?.userId ?? "anon",
        }}
        dashboardId={dashboard.record.id}
        pageId={dashboard.page.id}
        widgetInstanceIds={[item.instanceId]}
        preview={{
          widgetInstanceId: item.instanceId,
          slotKey: config.slotKey,
          selection,
        }}
      >
        {(data, loading, failed) =>
          failed ? (
            <WidgetCard title="Preview unavailable">
              <p className="text-sm text-pb-text-muted">
                These settings could not be previewed. Try again or choose a
                different selection.
              </p>
            </WidgetCard>
          ) : (
            renderWidget(item, data ?? { results: {} }, loading)
          )
        }
      </WidgetDataPreview>
    );
  };

  return (
    <div
      data-tour-id="home.dashboard"
      className="pb-motion-content-enter mx-auto w-full max-w-[90rem] px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12"
    >
      {/* No panel behind the greeting. It used to sit on a diagonal wash from
          the accent through nothing to the iris, inside its own rounded box —
          a shape defined on three sides that dissolved on the fourth, and one
          that inset the greeting from the lane every card below it lines up
          on. The greeting is typography on the page now, starting where the
          content starts. */}
      <header className="mb-8 sm:mb-10">
        <h1 className="text-display text-pb-text">
          {greetingName ? `Welcome back, ${greetingName}` : "Welcome back"}
        </h1>
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-pb-text-secondary">
          <span>{formatToday()}</span>
          <span aria-hidden className="text-pb-text-muted">
            ·
          </span>
          <span>Your work and operational signals, arranged your way.</span>
        </p>
      </header>

      {hasModules ? (
        <>
          {/* The two save notices stay hand-built: Alert paints the full soft
              fill, a 40% border, a glyph and py-2.5; no PR 3 look row
              approves that. */}
          {conflictNotice ? (
            <div
              role="status"
              className="pb-motion-content-enter mb-4 flex items-center justify-between rounded-md border border-pb-amber/40 bg-pb-amber-soft/20 px-3 py-2"
            >
              <p className="text-sm text-pb-text">
                Your change wasn&rsquo;t saved — the dashboard changed elsewhere
                and has been reloaded.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConflictNotice(false)}
              >
                Dismiss
              </Button>
            </div>
          ) : null}

          {errorNotice ? (
            <div
              role="alert"
              className="pb-motion-content-enter mb-4 flex items-center justify-between rounded-md border border-pb-red/40 bg-pb-red-soft/20 px-3 py-2"
            >
              <p className="text-sm text-pb-text">
                We couldn&rsquo;t save that change. Please try again.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setErrorNotice(false)}
              >
                Dismiss
              </Button>
            </div>
          ) : null}

          {/* The dashboard's four states in one place: a failed load is the
              error panel with its Retry (OK-31), the wait is the skeleton. */}
          <AsyncSection
            status={
              dashboard.isError
                ? "error"
                : dashboard.isLoading
                  ? "loading"
                  : "ready"
            }
            loadingLabel="your dashboard"
            errorTitle="We couldn’t load your dashboard right now."
            onRetry={dashboard.retryDashboard}
            skeleton={
              /* Its own dashboard container, as wide as the real dashboard's
                 (PB-5436): the skeleton is AsyncSection's, outside the
                 EntityDashboard root, so it needs the same width to switch
                 at the same point and not jump when the cards arrive. */
              <div className="@container/dashboard">
                <div className="grid gap-4 @min-[39rem]/dashboard:grid-cols-2 @min-[63rem]/dashboard:grid-cols-3">
                  {/* Kept hand-built: Card paints its 1px inset top hairline
                    (pb-hairline) on a flat default card, a look change no
                    PR 3 row approves. */}
                  {["one", "two", "three"].map((key) => (
                    <div
                      key={key}
                      className="space-y-3 rounded-xl border border-pb-border/80 bg-pb-card p-4"
                    >
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <KpiCatalogProvider value={kpiCatalog}>
              <VsmCatalogProvider value={vsmCatalog}>
                <SolveCatalogProvider value={solveCatalog}>
                  <QuickAccessCatalogProvider value={quickAccessCatalogValue}>
                    <WidgetActionsProvider value={actions}>
                      <EntityDashboard
                        variant="focused"
                        className="pb-home-dashboard p-0"
                        layout={dashboard.layout}
                        catalog={catalog}
                        renderWidget={renderWidget}
                        renderPreview={renderPreview}
                        onReorder={dashboard.onReorder}
                        onChangeWidth={dashboard.onChangeWidth}
                        onAddWidget={dashboard.onAddWidget}
                        onRemoveWidget={dashboard.onRemoveWidget}
                        onEditWidget={dashboard.onEditWidget}
                        toolbarLabel="Your dashboard"
                        emptyState={
                          /* Kept as today's 14px line: EmptyState draws a
                             medallion panel (sm: 11px type), a look change
                             no PR 3 row approves. */
                          <p className="text-sm text-pb-text-muted">
                            No widgets yet — add one to build your dashboard.
                          </p>
                        }
                      />
                    </WidgetActionsProvider>
                  </QuickAccessCatalogProvider>
                </SolveCatalogProvider>
              </VsmCatalogProvider>
            </KpiCatalogProvider>
          </AsyncSection>
        </>
      ) : (
        <NoModulesEmptyState />
      )}
    </div>
  );
}
